import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUserLevel } from '../redux/slices/userLevelSlice';
import getIsAdmin from '../utils/isAdmin';

export const useUserLevel = () => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.auth.user);
    const telegramUser = useSelector(state => state.telegram.telegramUser);
    const { data: userLevel, features, loading, error, lastFetch } = useSelector(state => state.userLevel);
    
    const currentUser = user || telegramUser;
    
    // Загружаем данные только один раз при монтировании первого компонента
    useEffect(() => {
        if (!currentUser) return;
        
        // Не загружаем если данные свежие (менее 5 минут)
        const CACHE_TIME = 5 * 60 * 1000;
        const isCacheValid = lastFetch && (Date.now() - lastFetch < CACHE_TIME);
        
        if (!userLevel && !loading && !isCacheValid) {
            dispatch(fetchUserLevel());
        }
    }, [currentUser, userLevel, loading, lastFetch, dispatch]);

    // Функция для проверки доступности функции - создаем сразу, не ждем загрузки
    const hasFeature = useCallback((featureName) => {
        const isAdmin = getIsAdmin(user, telegramUser);

        // Админы обходят все ограничения
        if (isAdmin) return true;

        if (!userLevel || !userLevel.features_list) return false;

        return userLevel.features_list[featureName] === true;
    }, [user, telegramUser, userLevel]);

    // Функция для проверки минимального уровня - создаем сразу, не ждем загрузки
    const hasMinLevel = useCallback((requiredLevel) => {
        const isAdmin = getIsAdmin(user, telegramUser);

        // Админы обходят все ограничения
        if (isAdmin) return true;

        if (!userLevel) return false;

        return userLevel.level >= requiredLevel;
    }, [user, telegramUser, userLevel]);

    // Функция для получения информации о функции
    const getFeatureInfo = (featureName) => {
        return features.find(feature => feature.name === featureName);
    };

    // Принудительное обновление уровня (после level-up)
    const refreshUserLevel = useCallback(() => {
        if (!currentUser) return;
        dispatch(fetchUserLevel());
    }, [currentUser, dispatch]);

    return {
        userLevel,
        features,
        loading,
        error,
        hasFeature,
        hasMinLevel,
        getFeatureInfo,
        refreshUserLevel,
        isAuthenticated: !!currentUser
    };
};
